// Clean implementation of routes and auth handling
import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { UserProvider, useUser } from "./UserContext";
import { ClientProvider } from "./contexts/ClientContext";
import { safeLogout } from "./logoutUtils";
import TrialExpiredDialog from "./components/TrialExpiredDialog";
import AuthLoading from "./pages/AuthLoading";
import { NonprofitRoute, ConsultantRoute, AuthenticatedRoute } from "./components/RouteGuards";
import AuthCallbackHandler from "./components/AuthCallbackHandler";
import { trackMarketingPageView } from "./marketingTag";
import { trackJourneyTelemetry } from "./utils/journeyTelemetry";

const HomePage = lazy(() => import("./HomePage"));
const LoginPage = lazy(() => import("./LoginPage"));
const RegistrationPage = lazy(() => import("./RegistrationPage"));
const LandingPage = lazy(() => import("./LandingPage"));
const GrantChat = lazy(() => import("./GrantChat"));
const SidebarLayout = lazy(() => import("./SidebarLayout"));
const GrantsList = lazy(() => import("./GrantsList"));
const OrgProfileEditor = lazy(() => import("./OrgProfileEditor/OrgProfileEditor"));
const NewGrantPage = lazy(() => import("./NewGrantPage"));
const DocumentsPage = lazy(() => import("./DocumentsPage"));
const ExploreFoundations = lazy(() => import("./ExploreFoundations"));
const PlansPage = lazy(() => import("./PlansPage"));
const TourPage = lazy(() => import("./TourPage"));
const AboutPage = lazy(() => import("./AboutPage"));
const ComparePage = lazy(() => import("./ComparePage"));
const InstrumentlComparePage = lazy(() => import("./InstrumentlComparePage"));
const GrantableComparePage = lazy(() => import("./GrantableComparePage"));
const FoundationDirectoryOnlineComparePage = lazy(() => import("./FoundationDirectoryOnlineComparePage"));
const FAQPage = lazy(() => import("./FAQPage"));
const ClientsList = lazy(() => import("./ClientsList"));
const ConsultantDashboard = lazy(() => import("./ConsultantDashboard"));
const AccountPage = lazy(() => import("./AccountPage"));
const ResourcesPage = lazy(() => import("./ResourcesPage"));
const BlogArticle = lazy(() => import("./components/BlogArticle"));
const HelpCenterPage = lazy(() => import("./HelpCenterPage"));
const PrivacyPolicyPage = lazy(() => import("./legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./legal/TermsOfServicePage"));
const AITransparencyPage = lazy(() => import("./legal/AITransparencyPage"));
const DPAPage = lazy(() => import("./legal/DPAPage"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const TrustCenterPage = lazy(() => import("./TrustCenterPage"));
const NotFoundPage = lazy(() => import("./NotFoundPage"));
const NonprofitOnboarding = lazy(() => import("./pages/NonprofitOnboarding"));
const ConsultantOnboarding = lazy(() => import("./pages/ConsultantOnboarding"));
const GrantProcessing = lazy(() => import("./pages/GrantProcessing"));

function AppShellLoading() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #173457 0%, #1e4573 100%)',
      color: '#fff',
      fontSize: '18px'
    }}>
      Loading...
    </div>
  );
}

// Inner component that uses UserContext
function RoutesContent() {
  const { instance, accounts, inProgress } = useMsal();
  const { userProfile, orgProfile, subscriptionState, refreshProfile } = useUser(); // Get profiles from UserContext
  const [activeAccount, setActiveAccount] = useState(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [showTrialExpiredDialog, setShowTrialExpiredDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Debug logging
  useEffect(() => {
    console.log("RoutesContent: userProfile from useUser():", userProfile ? "EXISTS" : "NULL");
    console.log("RoutesContent: orgProfile from useUser():", orgProfile ? "EXISTS" : "NULL");
  }, [userProfile, orgProfile]);

  useEffect(() => {
    trackMarketingPageView(location.pathname, location.search);
    trackJourneyTelemetry("journey_page_view", {
      page_path: location.pathname,
      has_query: Boolean(location.search)
    });
  }, [location.pathname, location.search]);

  // Log initial location details for diagnosing redirect handling in production
  useEffect(() => {
    console.log('[RouteMount] location.href=', window.location.href);
    console.log('[RouteMount] location.search=', window.location.search, 'location.hash=', window.location.hash);
    try {
      const captured = sessionStorage.getItem('initial_url_at_load');
      const capturedHash = sessionStorage.getItem('initial_hash_with_code');
      if (captured) console.log('[RouteMount] initial_url_at_load=', captured);
      if (capturedHash) console.log('[RouteMount] initial_hash_with_code=', capturedHash);
    } catch (e) {
      console.warn('[RouteMount] unable to read early capture keys', e);
    }
  }, []);

  // Mark MSAL initialized once startup interaction ends
  useEffect(() => {
    if (inProgress === InteractionStatus.None && !msalInitialized) {
      setMsalInitialized(true);
      console.log("MSAL provider ready. Accounts:", instance.getAllAccounts());
    }
  }, [inProgress, msalInitialized, instance]);

  // Set active account once available
  useEffect(() => {
    if (!msalInitialized) return;
    if (activeAccount) return;
    // Don't set active account on logout page
    if (window.location.pathname === '/logout') return;
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      setActiveAccount(accounts[0]);
      console.log("Active account set.");
    }
  }, [msalInitialized, accounts, activeAccount, instance]);

  // Invitation acceptance is now handled by AuthCallbackHandler (single source of truth)
  // Removed competing invitation logic from AppRoutes

  // Check for trial expiration and show dialog once per session
  useEffect(() => {
    if (!subscriptionState) return;
    
    const hasShownDialog = sessionStorage.getItem('gwc_trial_expired_shown') === 'true';
    
    if (subscriptionState.isTrialExpired && !hasShownDialog) {
      console.log('Trial expired - showing dialog');
      setShowTrialExpiredDialog(true);
    }
  }, [subscriptionState]);

  // Removed unified post-auth navigation effect; navigation now handled explicitly in RedirectHandler
  // and by user-initiated routes. This reduces race conditions with redirect processing.

  const handleLogout = useCallback(async () => {
    setActiveAccount(null);
    await safeLogout(instance, navigate);
  }, [instance, navigate]);

  function LogoutPage() {
    const hasCleared = React.useRef(false);
    
    useEffect(() => {
      if (!hasCleared.current) {
        hasCleared.current = true;
        setActiveAccount(null);
        
        // Preserve tour completion status before clearing storage
        const tourCompleted = localStorage.getItem('gwc_tour_completed');
        
        sessionStorage.clear();
        localStorage.clear();
        
        // Restore tour completion status
        if (tourCompleted) {
          localStorage.setItem('gwc_tour_completed', tourCompleted);
        }
      }
    }, []);
  
    return (
      <div style={{
        minHeight:'100vh',
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        background:'linear-gradient(135deg,#173457,#1e4573)',
        position:'relative'
      }}>
        <div style={{
          textAlign:'center',
          maxWidth:'500px',
          padding:'40px',
          background:'rgba(255,255,255,0.1)',
          backdropFilter:'blur(10px)',
          borderRadius:'20px',
          border:'1px solid rgba(255,255,255,0.2)',
          boxShadow:'0 8px 32px rgba(0,0,0,0.2)'
        }}>
          <h2 style={{
            color:'#fff',
            marginBottom:'15px',
            fontSize:'28px',
            fontWeight:'700'
          }}>
            You've been signed out successfully
          </h2>
          
          <p style={{
            color:'rgba(255,255,255,0.85)',
            marginBottom:'35px',
            fontSize:'16px',
            lineHeight:'1.6'
          }}>
            Thank you for using Imagagrant. Your session has been securely ended.
          </p>
          
          <button 
            onClick={() => navigate("/")} 
            style={{
              padding:'14px 40px',
              background:'#fcdf68',
              color:'#173457',
              border:'2px solid #fcdf68',
              borderRadius:'8px',
              cursor:'pointer',
              fontSize:'16px',
              fontWeight:'700',
              transition:'all 0.3s',
              boxShadow:'0 4px 15px rgba(255,215,0,0.3)'
            }} 
            onMouseOver={(e) => {
              e.target.style.background='#173457';
              e.target.style.color='#fcdf68';
              e.target.style.borderColor='#fcdf68';
              e.target.style.transform='translateY(-2px)';
              e.target.style.boxShadow='0 6px 20px rgba(255,215,0,0.4)';
            }} 
            onMouseOut={(e) => {
              e.target.style.background='#fcdf68';
              e.target.style.color='#173457';
              e.target.style.borderColor='#fcdf68';
              e.target.style.transform='translateY(0)';
              e.target.style.boxShadow='0 4px 15px rgba(255,215,0,0.3)';
            }}
          >
            Return to Home
          </button>
        </div>
        
        <p style={{
          position:'absolute',
          bottom:'20px',
          color:'rgba(255,255,255,0.6)',
          fontSize:'14px'
        }}>
          Need help? <a href="/contact" style={{color:'#fcdf68',textDecoration:'none',fontWeight:'600'}}>Contact Support</a>
        </p>
      </div>
    );
  }

  // Route guard to block expired trial users without active subscription
  function TrialGuard({ children }) {
    const hasActiveAccess = subscriptionState?.isActive === true;
    const isLocked = subscriptionState?.needsSubscription === true && !hasActiveAccess;
    
    if (isLocked) {
      return <Navigate to="/account" replace />;
    }
    
    return children;
  }

  return (
    <>
      {/* Trial Expired Dialog */}
      <TrialExpiredDialog 
        open={showTrialExpiredDialog}
        onClose={() => setShowTrialExpiredDialog(false)}
        trialTier={orgProfile?.accountType}
      />
      
      <Suspense fallback={<AppShellLoading />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/compare/instrumentl-vs-imagagrant" element={<InstrumentlComparePage />} />
        <Route path="/compare/grantable-vs-imagagrant" element={<GrantableComparePage />} />
        <Route path="/compare/foundation-directory-online-vs-imagagrant" element={<FoundationDirectoryOnlineComparePage />} />
        <Route path="/tour" element={<TourPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/trust-center" element={<TrustCenterPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<Navigate to="/plans" replace />} />
        <Route path="/register" element={<RegistrationPage />} />
        
        {/* Auth Flow Routes */}
        <Route path="/redirect" element={<AuthCallbackHandler />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/auth/loading" element={<AuthLoading />} />
        
        {/* Onboarding Routes (new user setup) */}
        <Route path="/onboarding/nonprofit" element={<NonprofitOnboarding />} />
        <Route path="/onboarding/consultant" element={<ConsultantOnboarding />} />
        
        {/* Nonprofit-Only Routes (route-guarded) */}
        <Route path="/landing" element={
          <NonprofitRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <LandingPage profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout} />
            </SidebarLayout>
          </NonprofitRoute>
        } />
        
        {/* Consultant-Only Routes (route-guarded) */}
        <Route path="/consultant-dashboard" element={
          <ConsultantRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <ConsultantDashboard />
            </SidebarLayout>
          </ConsultantRoute>
        } />
        
        {/* Shared Authenticated Routes (work for both types) */}
        <Route path="/grant-processing/:grantId" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <GrantProcessing />
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/grant/:grantId" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <GrantChat profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout} />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/clients" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <ClientsList />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/grants" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <GrantsList profile={userProfile} orgProfile={orgProfile} />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/org-profile" element={
          <AuthenticatedRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <OrgProfileEditor 
                profile={userProfile} 
                orgProfile={orgProfile} 
                onLogout={handleLogout}
                onSave={(updatedProfile) => {
                  sessionStorage.setItem("orgProfile", JSON.stringify(updatedProfile));
                  refreshProfile();
                }}
              />
            </SidebarLayout>
          </AuthenticatedRoute>
        } />
        <Route path="/client/:clientId/profile" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <OrgProfileEditor profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout} />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/newGrant" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <NewGrantPage />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/documents" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <DocumentsPage />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/explore-foundations" element={
          <AuthenticatedRoute>
            <TrialGuard>
              <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
                <ExploreFoundations />
              </SidebarLayout>
            </TrialGuard>
          </AuthenticatedRoute>
        } />
        <Route path="/app/trust-center" element={
          <AuthenticatedRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <TrustCenterPage inApp />
            </SidebarLayout>
          </AuthenticatedRoute>
        } />
        <Route path="/app/help-center" element={
          <AuthenticatedRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <HelpCenterPage inApp />
            </SidebarLayout>
          </AuthenticatedRoute>
        } />
        <Route path="/app/help-center/:slug" element={<Navigate to="/app/help-center" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegistrationPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/articles/:slug" element={<BlogArticle />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/help-center/:slug" element={<Navigate to="/help-center" replace />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/legal/terms" element={<TermsOfServicePage />} />
        <Route path="/legal/ai-transparency" element={<AITransparencyPage />} />
        <Route path="/legal/dpa" element={<DPAPage />} />
        <Route path="/account" element={
          <AuthenticatedRoute>
            <SidebarLayout profile={userProfile} orgProfile={orgProfile} onLogout={handleLogout}>
              <AccountPage profile={userProfile} orgProfile={orgProfile} refreshProfile={refreshProfile} />
            </SidebarLayout>
          </AuthenticatedRoute>
        } />
        <Route path="*" element={<NotFoundPage />} />
    </Routes>
      </Suspense>
    </>
  );
}

// Wrapper component that provides context
export default function AppRoutes() {
  return (
    <UserProvider>
      <ClientProvider>
        <RoutesContent />
      </ClientProvider>
    </UserProvider>
  );
}
